import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertCircle, Calculator, Edit, Loader2 } from 'lucide-react';
import { apiService, QaCostEstimationConfig, RagCostEstimationConfig } from '../../services/api';
import { toast } from 'sonner';

const eur = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);

/** Human-readable conversion preview for a USD→EUR rate, e.g. "1 USD → 0,92 € · 100 USD → 92,00 €". */
const ratePreview = (rate: number): string | null => {
  if (!Number.isFinite(rate) || rate < 0) return null;
  return `1 USD → ${eur(rate)} · 100 USD → ${eur(rate * 100)}`;
};

/**
 * Admin page for the cost-estimation settings consumed by the cost overview:
 * the fixed USD→EUR rate applied to QA/Evaluation eval-service run costs and
 * the RAG flat rates (per document / per retrieval call).
 */
export function CostSettingsPage() {
  const [qaConfig, setQaConfig] = useState<QaCostEstimationConfig | null>(null);
  const [ragConfig, setRagConfig] = useState<RagCostEstimationConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isQaDialogOpen, setIsQaDialogOpen] = useState(false);
  const [isRagDialogOpen, setIsRagDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [qaForm, setQaForm] = useState<QaCostEstimationConfig>({ usdToEurRate: 1.0 });
  const [ragForm, setRagForm] = useState<RagCostEstimationConfig>({ costPerDocumentEur: 0.001, costPerRetrievalEur: 0.0005 });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [qa, rag] = await Promise.all([
        apiService.getQaCostEstimationConfig(),
        apiService.getRagCostEstimationConfig(),
      ]);
      setQaConfig(qa);
      setRagConfig(rag);
      setQaForm(qa);
      setRagForm(rag);
    } catch (err: any) {
      console.error('Failed to load cost settings:', err);
      setError('Kosten-Einstellungen konnten nicht geladen werden');
      toast.error('Einstellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveQa = async () => {
    if (!(qaForm.usdToEurRate >= 0)) {
      toast.error('USD→EUR-Faktor muss ≥ 0 sein');
      return;
    }
    try {
      setIsSaving(true);
      await apiService.updateQaCostEstimationConfig(qaForm);
      toast.success('QA-Kosten-Einstellungen gespeichert');
      await loadConfigs();
      setIsQaDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to save QA cost settings:', err);
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveRag = async () => {
    if (!(ragForm.costPerDocumentEur >= 0) || !(ragForm.costPerRetrievalEur >= 0)) {
      toast.error('RAG-Pauschalen müssen ≥ 0 sein');
      return;
    }
    try {
      setIsSaving(true);
      await apiService.updateRagCostEstimationConfig(ragForm);
      toast.success('RAG-Pauschalen gespeichert');
      await loadConfigs();
      setIsRagDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to save RAG cost settings:', err);
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Lade Einstellungen...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-6 h-6" />
            Kosten-Einstellungen
          </h1>
          <p className="text-gray-600">Parameter der Kostenübersicht-Schätzung</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadConfigs}>Erneut versuchen</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calculator className="w-6 h-6" />
          Kosten-Einstellungen
        </h1>
        <p className="text-gray-600">
          Parameter der Kostenübersicht-Schätzung — Änderungen wirken sofort auf nachfolgende Abfragen
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>QA/Evaluation — USD→EUR-Faktor</CardTitle>
              <CardDescription>
                Fester Umrechnungsfaktor für die Eval-Service-Kosten von QA-Läufen (Judge + simulierter User, via
                OpenRouter in USD gemeldet). 1.0 = USD werden unverändert als EUR ausgewiesen.
              </CardDescription>
            </div>
            <Button onClick={() => { if (qaConfig) setQaForm(qaConfig); setIsQaDialogOpen(true); }}>
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-gray-500">USD→EUR-Faktor</Label>
              <p className="text-lg font-medium">{qaConfig?.usdToEurRate}</p>
              <p className="text-xs text-gray-400">Angewandt auf SUM(qa_runs.cost_usd) in der Kategorie „QA" der Kostenübersicht</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Umrechnung</Label>
              <p className="text-lg font-medium">{qaConfig ? ratePreview(qaConfig.usdToEurRate) : '–'}</p>
              <p className="text-xs text-gray-400">So viel EUR ergibt ein US-Dollar mit dem aktuellen Faktor</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>RAG-Pauschalen</CardTitle>
              <CardDescription>
                Pauschale Schätzwerte für Embedding- und Retrieval-Kosten (echtes RAG-Usage-Tracking existiert nicht).
              </CardDescription>
            </div>
            <Button onClick={() => { if (ragConfig) setRagForm(ragConfig); setIsRagDialogOpen(true); }}>
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-gray-500">Kosten pro Dokument (EUR)</Label>
              <p className="text-lg font-medium">{ragConfig?.costPerDocumentEur}</p>
              <p className="text-xs text-gray-400">Chunking + Embedding je indiziertem Dokument</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Kosten pro Retrieval-Call (EUR)</Label>
              <p className="text-lg font-medium">{ragConfig?.costPerRetrievalEur}</p>
              <p className="text-xs text-gray-400">1 Retrieval je Chat-Message in Sessions mit Knowledge-Base</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isQaDialogOpen} onOpenChange={setIsQaDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>USD→EUR-Faktor bearbeiten</DialogTitle>
            <DialogDescription>
              Faktor für die Umrechnung der Eval-Service-Kosten (USD) in die EUR-Kostenübersicht
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="usdToEurRate">USD→EUR-Faktor</Label>
            <Input
              id="usdToEurRate"
              type="number"
              min="0"
              step="0.01"
              value={qaForm.usdToEurRate}
              onChange={(e) => setQaForm({ usdToEurRate: parseFloat(e.target.value) })}
            />
            <p className="text-xs text-gray-500">
              z.B. 0.92 — fester Faktor, kein Tageskurs. Default 1.0 (USD as-is).
            </p>
            <p className="text-sm text-gray-700 bg-gray-50 border rounded-md px-3 py-2">
              {ratePreview(qaForm.usdToEurRate) ?? 'Ungültiger Wert — bitte eine Zahl ≥ 0 eingeben'}
            </p>
          </div>
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsQaDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveQa} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isRagDialogOpen} onOpenChange={setIsRagDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>RAG-Pauschalen bearbeiten</DialogTitle>
            <DialogDescription>
              Schätzwerte für die RAG-Kategorie der Kostenübersicht
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="costPerDocumentEur">Kosten pro Dokument (EUR)</Label>
              <Input
                id="costPerDocumentEur"
                type="number"
                min="0"
                step="0.0001"
                value={ragForm.costPerDocumentEur}
                onChange={(e) => setRagForm({ ...ragForm, costPerDocumentEur: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="costPerRetrievalEur">Kosten pro Retrieval-Call (EUR)</Label>
              <Input
                id="costPerRetrievalEur"
                type="number"
                min="0"
                step="0.0001"
                value={ragForm.costPerRetrievalEur}
                onChange={(e) => setRagForm({ ...ragForm, costPerRetrievalEur: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsRagDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveRag} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
